import { AdditionalEntitlement, MedicinePrescription } from '@dreryk/egabinet-prescription-frontend-api';
import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { ExtendedDosage } from '~libs.shared.prescription';

import { ActiveIngredientsQuantitiesTextValue } from '../../prescription/activeIngredients/ActiveIngredientsTextValue';
import { AdditionalEntitlementMultiSelect } from '../../prescription/additionalEntitlements/AdditionalEntitlementMultiSelect';
import { AdditionalRequirementsMultiSelect } from '../../prescription/additionalRequirements/AdditionalRequirementsMultiSelect';
import { AdditionalRequirement } from '../../prescription/additionalRequirements/additionalRequirements.types';
import { DosageInput } from '../../prescription/dosage/DosageInput';
import { IssuedFromDatePicker } from '../../prescription/issuedDate/IssuedFromDatePicker';
import { IssuedToTextValue } from '../../prescription/issuedDate/IssuedToTextValue';
import { DosageExceedingPackagesMessage } from '../../prescription/numericalDosage/DosageExceedingPackagesMessage';
import { MedicineSufficientForTextValue } from '../../prescription/numericalDosage/MedicineSufficientForTextValue';
import { DosageFrequencyNotAllowedMessage } from '../../prescription/numericalDosage/DosageFrequencyNotAllowedMessage';
import { DraftPrescriptionsFormController } from '../DraftPrescriptionsForm.types';

import { DraftStandardPrescription } from './DraftStandardPrescriptionField.types';
import { ExtendedDosageState } from './DraftStandardPrescriptionExtendedDosage.hooks';
import { TreatmentDaysCountState } from './DraftStandardPrescriptionField.hooks';
import { createDraftStandardPrescriptionsFieldNameFactory } from './DraftStandardPrescriptionField.utils';
import { DraftStandardPrescriptionGrid } from './DraftStandardPrescriptionGrid';
import { DraftStandardPrescriptionDosageSlot } from './DraftStandardPrescriptionDosageSlot';
import { DraftStandardPrescriptionQuantitySlot } from './DraftStandardPrescriptionQuantitySlot';
import { DraftStandardPrescriptionTreatmentDaysExceededSlot } from './DraftStandardPrescriptionTreatmentDaysExceededSlot';
import { DraftStandardPrescriptionTreatmentDaysSlot } from './DraftStandardPrescriptionTreatmentDaysSlot';

type DraftStandardPrescriptionFieldGridProps = Pick<
   DraftPrescriptionsFormController,
   'register' | 'trigger' | 'setValue' | 'setError' | 'formState' | 'watch'
> & {
   readonly index: number;
   readonly data: DraftStandardPrescription['data'];
   readonly extendedDosageValue: ExtendedDosage | null;
   readonly initialQuantity: number | null;
   readonly medicinePrescription: MedicinePrescription;
   readonly medicine: MedicineSimple;
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly extendedDosage: ExtendedDosageState;
   readonly treatmentDaysCount: TreatmentDaysCountState;
   readonly underTreatmentSupervision: boolean;
   readonly dosageFieldRequired: boolean;
   readonly dosageFrequencyNotAllowed: boolean;
   readonly dosageFrequencyValidated: boolean;
   readonly computedAdditionalEntitlements: AdditionalEntitlement[] | undefined;
   readonly computedAdditionalRequirements: AdditionalRequirement[] | undefined;
   readonly activeIngredientsQuantity: number | null;
   readonly medicineSufficiencyCount: number | undefined;
};

// eslint-disable-next-line max-lines-per-function
export const DraftStandardPrescriptionFieldGrid = (props: DraftStandardPrescriptionFieldGridProps): JSX.Element => {
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(props.index);
   const treatmentDaysCountExceeded = props.extendedDosage.active
      ? props.extendedDosage.exceeded
      : props.treatmentDaysCount.exceeded;
   const dosageExceeding = !props.extendedDosage.active && props.treatmentDaysCount.dosageExceedingPackages;
   const currentDosage =
      props.watch<ReturnType<typeof createFieldName>, string | null>(createFieldName('dosage')) ?? props.data.dosage;

   return (
      <DraftStandardPrescriptionGrid
         dosage={
            <DosageInput
               register={props.register}
               trigger={props.trigger}
               name={createFieldName('dosage')}
               defaultValue={currentDosage}
               required={props.dosageFieldRequired}
            />
         }
         issuedFrom={
            <IssuedFromDatePicker
               register={props.register}
               name={createFieldName('issuedFrom')}
               defaultValue={props.data.issuedFrom}
            />
         }
         quantity={
            <DraftStandardPrescriptionQuantitySlot
               register={props.register}
               trigger={props.trigger}
               name={createFieldName('quantity')}
               defaultValue={props.initialQuantity}
               extendedDosageActive={props.extendedDosage.active}
               packagesCountEditable={props.extendedDosage.packagesCountEditable}
               dosageExceedingPackages={props.treatmentDaysCount.dosageExceedingPackages}
            />
         }
         issuedTo={
            <IssuedToTextValue
               name={createFieldName('issuedTo')}
               setValue={props.setValue}
               issuedFrom={props.watch(createFieldName('issuedFrom'))}
               additionalRequirements={props.watch(createFieldName('additionalRequirements'))}
               defaultValue={props.data.issuedTo}
            />
         }
         activeIngredients={
            <ActiveIngredientsQuantitiesTextValue
               medicinePrescription={props.medicinePrescription}
               quantity={props.activeIngredientsQuantity}
            />
         }
         medicineSufficientFor={
            <MedicineSufficientForTextValue medicineSufficiencyCount={props.medicineSufficiencyCount} />
         }
         additionalEntitlements={
            <AdditionalEntitlementMultiSelect
               register={props.register}
               name={createFieldName('additionalEntitlements')}
               defaultValue={props.computedAdditionalEntitlements}
               additionalEntitlements={props.medicinePrescription.additionalEntitlements}
            />
         }
         additionalRequirements={
            <AdditionalRequirementsMultiSelect
               register={props.register}
               name={createFieldName('additionalRequirements')}
               defaultValue={props.computedAdditionalRequirements}
            />
         }
         numericalDosage={
            <DraftStandardPrescriptionDosageSlot
               register={props.register}
               trigger={props.trigger}
               setValue={props.setValue}
               setError={props.setError}
               formState={props.formState}
               watch={props.watch}
               index={props.index}
               extendedDosageFieldName={createFieldName('extendedDosage')}
               dosageFieldName={createFieldName('dosage')}
               dosage={currentDosage}
               extendedDosage={props.extendedDosageValue}
               extendedDosageActive={props.extendedDosage.active}
               required={props.underTreatmentSupervision}
               dosageFrequencyNotAllowed={props.dosageFrequencyNotAllowed}
               dosageFrequencyValidated={props.dosageFrequencyValidated}
               initialNumericalDosageFrequency={props.data.numericalDosageFrequency}
               initialNumericalDosageQuantity={props.data.numericalDosageQuantity}
               initialDosageUnit={props.data.dosageUnit}
               medicine={props.medicine}
               reimbursements={props.medicinePrescription.patientMedicine?.reimbursements ?? []}
               visitDiagnoses={props.visitDiagnoses}
            />
         }
         treatmentDaysCount={
            <DraftStandardPrescriptionTreatmentDaysSlot
               register={props.register}
               trigger={props.trigger}
               name={createFieldName('treatmentDaysCount')}
               extendedDosageActive={props.extendedDosage.active}
               extendedDosageTreatmentDaysCount={props.extendedDosage.treatmentDaysCount}
               initialTreatmentDaysCount={props.data.treatmentDaysCount}
               medicineSufficiencyCount={props.treatmentDaysCount.medicineSufficiencyCount}
               underTreatmentSupervision={props.underTreatmentSupervision}
               maxValue={
                  props.extendedDosage.active ? props.extendedDosage.maxValue : props.treatmentDaysCount.maxValue
               }
               exceeded={
                  props.extendedDosage.active
                     ? props.extendedDosage.exceeded || props.dosageFrequencyNotAllowed
                     : props.treatmentDaysCount.exceeded
               }
               editable={props.treatmentDaysCount.editable}
            />
         }
         treatmentDaysCountExceeded={
            treatmentDaysCountExceeded ? (
               <DraftStandardPrescriptionTreatmentDaysExceededSlot
                  setValue={props.setValue}
                  name={createFieldName('additionalRequirements')}
                  maxValue={
                     props.extendedDosage.active
                        ? props.extendedDosage.exceededMaxValue
                        : props.treatmentDaysCount.exceededMaxValue
                  }
                  changeToPrescriptionForYearAvailable={
                     props.extendedDosage.active
                        ? props.extendedDosage.changeToPrescriptionForYearAvailable
                        : props.treatmentDaysCount.changeToPrescriptionForYearAvailable
                  }
                  additionalRequirements={props.watch(createFieldName('additionalRequirements'))}
               />
            ) : undefined
         }
         dosageExceedingPackages={dosageExceeding ? <DosageExceedingPackagesMessage /> : undefined}
         dosageFrequencyNotAllowed={props.dosageFrequencyNotAllowed ? <DosageFrequencyNotAllowedMessage /> : undefined}
      />
   );
};
